import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, FolderKanban, Plus, Send, Loader2, UserPlus, Trash2, AlertTriangle, Camera } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { StaggerList, StaggerItem } from '../../components/animations/StaggerList';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface Client {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string;
  userCount: number;
  projectCount: number;
  createdAt: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface Project {
  id: string;
  name: string;
  status: string;
  workItemCount: number;
  pendingApprovalCount: number;
}

export function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const [clientRes, usersRes, projectsRes] = await Promise.all([
          api.get<Client>(`/organizations/${id}`),
          api.get<User[]>(`/organizations/${id}/users`),
          api.get<Project[]>(`/projects?organizationId=${id}`),
        ]);
        setClient(clientRes.data);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
        setError(null);
      } catch (err) {
        setError('Failed to load client details');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    if (id) fetchData();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error || 'Client not found'}</p>
        <Link to="/clients">
          <Button variant="secondary">Back to Clients</Button>
        </Link>
      </div>
    );
  }

  const formatLastLogin = (date: string | null) => {
    if (!date) return 'Never';
    const diff = Date.now() - new Date(date).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const resetInviteModal = () => {
    setInviteName('');
    setInviteEmail('');
    setInviteModalOpen(false);
  };

  const handleInvite = async () => {
    if (!inviteName.trim() || !inviteEmail.trim()) {
      toast.error('Please enter name and email');
      return;
    }

    setIsInviting(true);
    try {
      const response = await api.post<User>(`/organizations/${id}/invite`, {
        name: inviteName.trim(),
        email: inviteEmail.trim(),
      });

      toast.success(`Invitation sent to ${inviteEmail}`);
      setUsers([...users, response.data]);
      resetInviteModal();
    } catch (err: any) {
      toast.error(err.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText !== client?.name) {
      toast.error('Please type the client name to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete(`/organizations/${id}`);
      toast.success('Client deleted successfully');
      navigate('/clients');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete client');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetDeleteModal = () => {
    setDeleteConfirmText('');
    setDeleteModalOpen(false);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.upload(`/uploads/organization-logo/${id}`, formData);
      const data = response.data as { logoUrl: string };

      setClient((prev) => prev ? { ...prev, logoUrl: data.logoUrl } : null);
      toast.success('Logo updated successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Hidden file input for logo */}
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleLogoUpload}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start justify-between"
      >
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => logoInputRef.current?.click()}
            disabled={isUploadingLogo}
            className="relative w-16 h-16 rounded-2xl overflow-hidden shadow-lg group"
            style={{
              backgroundColor: client.primaryColor,
              boxShadow: `0 8px 30px ${client.primaryColor}40`,
            }}
          >
            {client.logoUrl ? (
              <img
                src={client.logoUrl}
                alt={client.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white font-bold text-2xl">
                {client.name.charAt(0)}
              </span>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {isUploadingLogo ? (
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              ) : (
                <Camera className="w-6 h-6 text-white" />
              )}
            </div>
          </motion.button>
          <div>
            <h1 className="text-3xl font-bold">{client.name}</h1>
            <p className="text-text-secondary">{client.slug}</p>
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={() => setDeleteModalOpen(true)}
          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </Button>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Section */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <Card hover={false}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-accent" />
                Team Members ({users.length})
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => setInviteModalOpen(true)}>
                <Plus className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {users.length === 0 ? (
                <div className="p-4 text-center text-text-muted">
                  <UserPlus className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No team members yet</p>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-3"
                    onClick={() => setInviteModalOpen(true)}
                  >
                    Invite First Member
                  </Button>
                </div>
              ) : (
                <StaggerList className="divide-y divide-border">
                  {users.map((user) => (
                    <StaggerItem
                      key={user.id}
                      className="p-4 hover:bg-bg-tertiary/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white font-semibold">
                          {user.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-sm text-text-muted truncate">
                            {user.email}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-text-muted mt-2 ml-13">
                        Last active: {formatLastLogin(user.lastLoginAt)}
                      </p>
                    </StaggerItem>
                  ))}
                </StaggerList>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Projects Section */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card hover={false}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FolderKanban className="w-5 h-5 text-accent" />
                Projects ({projects.length})
              </CardTitle>
              <Link to={`/projects/new?organizationId=${id}`}>
                <Button size="sm">
                  <Plus className="w-4 h-4" />
                  New Project
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {projects.length === 0 ? (
                <div className="p-8 text-center text-text-muted">
                  <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No projects yet</p>
                  <Link to={`/projects/new?organizationId=${id}`}>
                    <Button variant="secondary" size="sm" className="mt-4">
                      Create First Project
                    </Button>
                  </Link>
                </div>
              ) : (
                <StaggerList className="divide-y divide-border">
                  {projects.map((project) => (
                    <StaggerItem key={project.id}>
                      <Link
                        to={`/projects/${project.id}`}
                        className="block p-4 hover:bg-bg-tertiary/50 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-bg-tertiary flex items-center justify-center">
                              <FolderKanban className="w-6 h-6 text-accent" />
                            </div>
                            <div>
                              <h3 className="font-semibold">{project.name}</h3>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-sm text-text-muted">
                                  {project.workItemCount} items
                                </span>
                                {project.pendingApprovalCount > 0 && (
                                  <Badge variant="warning">
                                    {project.pendingApprovalCount} pending
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <Badge
                            variant={project.status === 'active' ? 'success' : 'default'}
                          >
                            {project.status}
                          </Badge>
                        </div>
                      </Link>
                    </StaggerItem>
                  ))}
                </StaggerList>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Invite User Modal */}
      <Modal
        isOpen={inviteModalOpen}
        onClose={resetInviteModal}
        title="Invite Team Member"
        description={`Invite a new user to ${client.name}`}
      >
        <div className="space-y-4">
          <Input
            label="Name"
            placeholder="John Smith"
            value={inviteName}
            onChange={(e) => setInviteName(e.target.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            placeholder="john@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <p className="text-sm text-text-muted">
            They'll receive an email with a link to access the client portal.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetInviteModal}>
              Cancel
            </Button>
            <Button
              onClick={handleInvite}
              disabled={!inviteName.trim() || !inviteEmail.trim() || isInviting}
              isLoading={isInviting}
            >
              <Send className="w-4 h-4" />
              Send Invitation
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={resetDeleteModal}
        title="Delete Client"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-500">This action cannot be undone</p>
              <p className="text-sm text-text-secondary mt-1">
                Deleting this client will permanently remove all associated users, projects, work items, and comments.
              </p>
            </div>
          </div>

          <div>
            <p className="text-sm text-text-secondary mb-2">
              To confirm, type <span className="font-semibold text-text">{client.name}</span> below:
            </p>
            <Input
              placeholder={client.name}
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={resetDeleteModal}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteConfirmText !== client.name || isDeleting}
              isLoading={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="w-4 h-4" />
              Delete Client
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
